/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { of } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { MailAttachmentChipsComponent, MAX_MAIL_ATTACHMENT_BYTES } from '@src/app/shared/mail-attachment-chips/mail-attachment-chips.component';
import { Person } from '@src/app/model/person';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { Attachment } from '@src/app/model/attachment';
import { AbstractObject } from '@src/app/model/abstract-object';
import { MailItem } from '@src/app/model/mailMerge';
import { Field } from '@src/app/shared/field/Field';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { CommsTemplateType } from '@src/app/model/CommsTemplate';
import { maxChars, minChars, required } from '@src/app/shared/validators';
import { Role } from '@src/app/model/role';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { MessageService } from '@src/app/shared/message.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { RequestService } from '@src/app/modules/crm/request.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MAIL_STATUS } from '@src/app/model/person';
import { PreviewMailDialogComponent } from '@src/app/modules/social/comms-template-page/preview-mail-dialog.component';

type SendMailMode = 'send' | 'test' | 'attach';

export interface SendPersonMailData {
    person: Person;
    sr?: ServiceRequest;
    availableAttachments: Attachment[];
}

@Component({
    selector: 'app-send-person-mail',
    imports: [DialogBannerComponent, MatDialogModule, CtlHolderComponent, MatButtonModule, MatTooltipModule, MailAttachmentChipsComponent],
    templateUrl: './send-person-mail.component.html',
    styleUrl: './send-person-mail.component.scss',
})
export class SendPersonMailComponent {
    dialogRef = inject<MatDialogRef<SendPersonMailComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA) as SendPersonMailData;

    personService = inject(PersonService);
    msgSvc = inject(MessageService);
    cds = inject(ConfirmDialogService);
    dialog = inject(MatDialog);
    srSvc = inject(RequestService);
    currentUser = inject(CurrentUserService).currentUser;

    selectedAttachments: Attachment[] = [];
    isSending = false;

    statementOptions: AbstractObject[] = this.buildStatementOptions();

    statementTypeField = FormPicklistComponent.make('Statement Type', 'statementType', null, {
        items: [
            { id: 'shortStatement', name: 'Outstanding items only' },
            { id: 'fullStatement', name: 'All credit & debit transactions' },
        ]
    }, { visible: Field.noForm }).setupControl();

    statementField = FormPicklistComponent.make('Include Statement', 'statementUnitId', null, {
        items: this.statementOptions,
        refreshes: [(selected: AbstractObject) => {
            this.statementTypeField.visible = selected?.id ? Field.formOnly : Field.noForm;
        }]
    }).setupControl();

    subjectField = FormTextComponent.make('Subject', 'subject', {
        validators: [required, maxChars(200), minChars(10)]
    }).setupControl();

    contentField = FormRichTextComponent.make('Content', 'description', {})
        .setCommsTemplate(CommsTemplateType.PERSON)
        .setupControl();

    get attachmentSizeExceeded(): boolean {
        return this.selectedAttachments.reduce((sum, a) => sum + (a.size || 0), 0) > MAX_MAIL_ATTACHMENT_BYTES;
    }

    get hasValidEmail(): boolean {
        return Person.validEmail(this.data.person.email) && this.data.person.mailStatus === MAIL_STATUS.ok;
    }

    get sendDisabledReason(): string {
        if (!Person.validEmail(this.data.person.email)) {
            return $localize`Contact does not have a valid email address`;
        }
        return $localize`Contact's mail status is: ${MAIL_STATUS.getDescription(this.data.person.mailStatus)}`;
    }

    readonly previewTooltip = $localize`Preview the merged content and any selected attachments — nothing is sent or saved.`;

    get sendTooltip(): string {
        return $localize`Send the email to ${this.data.person.fullName} <${this.data.person.email}>, and record it against their history.`;
    }

    get testTooltip(): string {
        return $localize`Send a test copy to your own address (${this.currentUser?.email}) so you can check it. It will not be sent to ${this.data.person.fullName}, and will not be recorded against them.`;
    }

    get attachTooltip(): string {
        return $localize`Generate a PDF from the content and attach it to ${this.data.person.fullName}. No email will be sent.`;
    }

    constructor() {
        const mailItem = this.buildMailItem();
        this.subjectField.setValue(mailItem, false);
        this.contentField.setValue(mailItem, false);
        this.statementField.setValue(mailItem, false);
        this.statementTypeField.setValue(mailItem, false);
    }

    doPreview() {
        const s = this.subjectField.getFormValue() as string;
        const c = this.contentField.getFormValue() as string;
        const stmtUnitId = this.statementField.getFormValue() as string;
        const stmtType = this.statementTypeField.getFormValue() as string;

        this.personService.previewMail(this.data.person, s, c, this.data.sr, stmtUnitId, stmtType)
            .subscribe(result => {
                if (result) {
                    this.dialog.open(PreviewMailDialogComponent, { data: { title: $localize`Preview`, html: result.html }, width: '80vw', maxWidth: '80vw' });
                }
            });
    }

    confirmSend() {
        this.cds.openConfirm($localize`Send Email?`, this.sendTooltip)
            .subscribe(r => { if (r === 'OK') { this.submit('send'); } });
    }

    confirmTest() {
        this.cds.openConfirm($localize`Send Test Email?`, this.testTooltip)
            .subscribe(r => { if (r === 'OK') { this.submit('test'); } });
    }

    confirmAttach() {
        this.cds.openConfirm($localize`Attach as PDF?`, this.attachTooltip)
            .subscribe(r => { if (r === 'OK') { this.submit('attach'); } });
    }

    private submit(mode: SendMailMode) {
        this.isSending = true;
        const s = this.subjectField.getFormValue() as string;
        const c = this.contentField.getFormValue() as string;
        const stmtUnitId = this.statementField.getFormValue() as string;
        const stmtType = this.statementTypeField.getFormValue() as string;

        this.personService.sendMail(
            this.data.person, s, c, mode, this.selectedAttachments, this.data.sr, stmtUnitId, stmtType
        ).pipe(
            tap(() => this.msgSvc.show(this.resultMessage(mode))),
            concatMap(() => this.afterSubmit(mode))
        ).subscribe({
            next: result => {
                this.isSending = false;
                if (mode !== 'test') {
                    this.dialogRef.close(result);
                }
            },
            error: () => this.isSending = false,
        });
    }

    private afterSubmit(mode: SendMailMode): Observable<ServiceRequest | Person> {
        if (mode === 'send' && this.data.sr) {
            return this.srResponse();
        }
        return of(this.data.person);
    }

    private resultMessage(mode: SendMailMode): string {
        if (mode === 'test') {
            return $localize`Test sent to ${this.currentUser?.email} — check your inbox.`;
        }
        if (mode === 'attach') {
            return $localize`Your document has been queued for generation. Wait a few moments and refresh your screen (or check back later) to see the new attachment.`;
        }
        return $localize`Your message has been queued for sending. Wait a few moments and refresh your screen (or check back later) to see the sent mail and any new statement attachments.`;
    }

    private buildMailItem(): MailItem {
        const a = new MailItem();
        a.toAddress = this.data.person.fullName + ' <' + this.data.person.email + '>';
        if (this.data.sr?.refNr) {
            a.subject = `${this.data.sr.teamId}:${this.data.sr.refNr} - ${this.data.sr.title}`;
        }
        a['statementUnitId'] = '';
        a['statementType'] = 'shortStatement';
        return a;
    }

    private buildStatementOptions(): AbstractObject[] {
        const unitRoles = this.data.person?.personUnits?.filter(pur =>
            pur.unitId != null &&
            !pur.ownerChangeId &&
            (pur.role?.globalRoleId === Role.ROLE.OWNER.id || pur.role?.globalRoleId === Role.ROLE.OWNER_OCCUPIER.id)
        ) ?? [];
        const options: AbstractObject[] = [{ id: '', name: 'No Statement' }];
        if (unitRoles.length > 1) {
            options.push({ id: 'all', name: 'All Units' });
        }
        unitRoles.forEach(pur =>
            options.push({ id: pur.unitId, name: `${pur.unit?.name} (${pur.unit?.address || ''}) ` })
        );
        return options;
    }

    private srResponse(): Observable<ServiceRequest> {
        return this.srSvc.promptExpectResponse(this.data.sr);
    }
}
